<template>
  <div>
    <div>
      <BaseButton
        :text="`Back to 'Your details'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="backToYourDetailsStep"
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Property details
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <ValidationObserver
      ref="observerFormPropertyDetails"
      v-slot="{ validate, invalid }"
    >
      <v-form
        ref="form"
        @submit.prevent="validate().then(() => onSubmit(invalid))"
      >
        <BaseLabel
          v-if="!isShowPropertyDetailsFields && !isEnteringAddressManually"
          class="mt-10"
          text="Find your property"
          :hintText="ADDRESS_FINDER_INPUT_DESCRIPTION"
        ></BaseLabel>
        <BaseAddressLookup
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Find your property',
            },
          }"
          :loading="false"
          :placeholder="
            $vuetify.breakpoint.lgAndUp
              ? 'Start typing your address or postcode...'
              : 'Start typing...'
          "
          data-uiid="InsuredAddress"
          manualFormUiid="InsuredAddress"
          id="address-lookup-insuredAddress"
          showManualAddressForm
          manualAddressFormText="Your address"
          :hideDetails="false"
          :maxLengthStreet="60"
          :address="insuredAddress"
          @address-selected="
            (response) =>
              addressSelected(response, insuredAddress, (truncateEnd = 60))
          "
          @use-address-finder="useAddressFinder"
          @enter-address-manually="isEnteringAddressManually = true"
          @is-complete-fill-address="onCompleteFillAddress"
          ref="autocomplete"
        />

        <template
          v-if="isShowPropertyDetailsFields || isEnteringAddressManually"
        >
          <BaseLabel
            :class="[{ 'mt-10': !isEnteringAddressManually }]"
            :text="LABEL_CONSTANTS.PROPERTY_TYPE"
          />
          <BaseRadio
            :style="{ 'max-width': $vuetify.breakpoint.lgAndUp ? '700px' : '' }"
            class="mt-2"
            :hideDetails="false"
            :rules="{
              required: {
                required: true,
                fieldLabel: LABEL_CONSTANTS.PROPERTY_TYPE_LABEL_MSG,
              },
            }"
            v-model="questionPropertyType.Value"
            pslStyle="image"
            :items="propertyTypeOptions"
            :imageStyleBorderRadius="10"
            dataUiid="PropertyType"
          />

          <BaseLabel
            class="mt-3 mb-2"
            :text="LABEL_CONSTANTS.NUMBER_OF_BEDROOMS_LABEL_QUESTION"
            :hintText="LABEL_CONSTANTS.NUMBER_OF_BEDROOMS_HELP_TEXT"
          />
          <CounterBaseQuestion
            dataUiid="NumberOfBedrooms"
            :answerControlCounterOptions="
              ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS
            "
            v-model="questionNumberOfBedrooms.Value"
            :key="`${questionNumberOfBedrooms.Value}_NumberOfBedrooms`"
            class="mb-10"
            aria-label="Number of bedrooms"
          />
        </template>

        <BaseButton
          rounded
          class="font-weight-bold continue-button primary--text mt-5"
          height="60"
          type="submit"
          text="GET QUOTES"
          :color="'psorange'"
          :fontSize="'font-20'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '263' : '100%'"
        />
      </v-form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="TENANTS_CONTENTS_ELIGIBILITY"
          :inforMsg="TENANTS_CONTENTS_ELIGIBILITY_INFO_MSG"
          :title="title"
          @confirmEligibility="createQuote()"
          :customHandleConfirmEligibility="true"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          :title="title"
          :gettingQuoteMessage="GETTING_QUOTE_TENANTS_CONTENTS_MESSAGE"
          :image-source="defaqtoImageUrl"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>

<script>
import { scrollTop } from "@/common/utils";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/common/eventbus.js";
import { addressSelected } from "@/common/addresses";
import {
  NO_CLAIMS_CONTENTS_ITEMS,
  setItemDataUiids,
  ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS,
  TENANTS_CONTENT_PROPERTIES_TYPE_ITEMS,
} from "@/constants/component.constants";
import {
  TENANTS_CONTENTS_ELIGIBILITY,
  TENANTS_CONTENTS_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { GETTING_QUOTE_TENANTS_CONTENTS_MESSAGE } from "@/constants/message.constants";
import { ADDRESS_FINDER_INPUT_DESCRIPTION } from "@/constants/description.constants";
import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup.vue";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseRadio from "@/components/BaseComponents/BaseRadio.vue";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet.vue";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import Eligibility from "@/views/Dialog/Eligibility.vue";
import CounterBaseQuestion from "@/components/BaseComponents/CounterBaseQuestion.vue";

export default {
  name: "PropertyDetailsPage",
  components: {
    BaseButton,
    BaseLabel,
    BaseRadio,
    ValidationObserver,
    BaseAddressLookup,
    EligibilityLoading,
    EligibilityNotMet,
    EligibilityBaseDialog,
    Eligibility,
    CounterBaseQuestion,
  },
  data() {
    return {
      LABEL_CONSTANTS,
      NO_CLAIMS_CONTENTS_ITEMS,
      TENANTS_CONTENT_PROPERTIES_TYPE_ITEMS,
      TENANTS_CONTENTS_ELIGIBILITY_INFO_MSG,
      TENANTS_CONTENTS_ELIGIBILITY,
      ANSWER_CONTROL_BED_ROOM_COUNTER_OPTIONS,
      GETTING_QUOTE_TENANTS_CONTENTS_MESSAGE,
      insuredAddress: {},
      questionPropertyType: {},
      questionNumberOfBedrooms: {},
      isShowPropertyDetailsFields: false,
      isEnteringAddressManually: false,
      defaqtoImageUrl: require("@/assets/images/defaqto/defaqto-rating-5-star.svg"),
      ADDRESS_FINDER_INPUT_DESCRIPTION,
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "restoringLastStep",
      "coverAnswers",
      "questionMap",
    ]),
    noClaimContentOptions() {
      return setItemDataUiids("NoClaimsContents", NO_CLAIMS_CONTENTS_ITEMS);
    },
    propertyTypeOptions() {
      return setItemDataUiids(
        "PropertyType",
        TENANTS_CONTENT_PROPERTIES_TYPE_ITEMS
      );
    },
  },
  created() {
    scrollTop();
    this.initDataFromStorage();
  },
  mounted() {
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPropertyDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "refreshSessionAndNextStep",
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "setIsExpiredFlag",
      "setCurrentRefObserverForm",
      "setCoverAnswers",
    ]),
    addressSelected,
    backToYourDetailsStep() {
      this.setRestoringLastStep(false);
      this.$router.push({ name: ROUTE_NAME.TENANTS_CONTENTS.B2B.YOUR_DETAILS });
    },
    initDataFromStorage() {
      this.questionPropertyType = this.questionMap.PropertyType;
      this.questionNumberOfBedrooms = this.questionMap.NumberOfBedrooms;
      // insured address
      this.insuredAddress.PostCode = this.questionMap.InsuredAddressPostCode;
      this.insuredAddress.Street = this.questionMap.InsuredAddressStreet;
      this.insuredAddress.District = this.questionMap.InsuredAddressDistrict;
      this.insuredAddress.Town = this.questionMap.InsuredAddressTown;
      this.insuredAddress.County = this.questionMap.InsuredAddressCounty;

      if (this.insuredAddress.PostCode.Value) {
        this.isShowPropertyDetailsFields = true;
      }
    },
    async onSubmit(invalid) {
      if (invalid) {
        EventBus.$emit("validation-error", "Validation error occurred");
        return;
      }
      this.$refs.dialog.open("Eligibility check");
    },
    createQuote() {
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");
      this.questionMap.TenantsContentsEligibility.Value = true;

      // set default cover answer
      if (!this.coverAnswers || this.coverAnswers.length == 0) {
        this.setCoverAnswers([
          {
            InterfaceKey: "ContentsCoverLevel",
            Value: "TenThousand",
          },
          {
            InterfaceKey: "ExcessContents",
            Value: "OneHundred",
          },
          {
            InterfaceKey: "PersonalPossessionsOver2500",
            Value: false,
          },
          {
            InterfaceKey: "ContentsItemsToSpecify",
            Value: false,
          },
          {
            InterfaceKey: "LegalExpenses",
            Value: false,
          },
        ]);
      }

      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then(() => {
          this.setIsExpiredFlag(false);
          this.$refs.dialog.cancel();
          this.refreshSessionAndNextStep(
            ROUTE_NAME.TENANTS_CONTENTS.B2B.QUOTATION_RESULT
          );
        })
        .catch(() => {
          this.$refs.dialogLoading.cancel();
          setTimeout(() => {
            let elm = document.querySelector(".error-message");
            elm?.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "nearest",
            });
          }, 300);
        });
    },
    onCompleteFillAddress(value) {
      this.isShowPropertyDetailsFields = value;
    },
    useAddressFinder() {
      //Reset everything to start address look up over
      this.isEnteringAddressManually = false;
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.find-property) {
  width: 100% !important;
  margin-top: 6px;
  .v-input__slot {
    height: 48px !important;
  }
}
@media screen and (min-width: $grid-breakpoints-lg) {
  :deep(.find-property) {
    max-width: 480px;
  }
  :deep(.sub-help-text) {
    width: 663px !important;
  }
}
</style>
